import React from 'react';
import { Task, FullProject, ProjectMetadata, Idea } from '../../types';

export type MetricsTabId = 'overview' | 'roadmap' | 'time' | 'projects' | 'library' | 'ideaPool' | 'annualReport' | 'activity';
export type PoolTabId = 'activeProjects' | 'archive' | 'library' | 'ideaPool';
export type AnalysisTabId = 'swot' | 'stakeholder' | 'risk' | 'toc' | 'raci' | 'participation';
export type CommunicationTabId = 'board' | 'database' | 'network' | 'archive';
export type AIAssistantTabId = 'inbox' | 'planning' | 'protocol';

export interface ViewDragHandlers {
  onDragStart?: (event: unknown) => void;
  onDragEnd?: (event: unknown) => boolean | void;
  onDragOver?: (event: unknown) => void;
  onDragCancel?: (event: unknown) => void;
}

export interface GanttHeaderMonth {
  label: string;
  days: number;
  startIndex?: number;
}

export const EMPTY_METADATA: ProjectMetadata = {};
export const DEFAULT_INBOX_ITEMS: NonNullable<ProjectMetadata['inboxItems']> = [];
export const DEFAULT_PROCESSED_ITEMS: NonNullable<ProjectMetadata['processedItems']> = [];

export interface AppViewRouterProps {
  // Layout & View flags
  isProjectView: boolean;
  isConceptView: boolean;
  isRightSidebarOpen: boolean;
  isProjectEditor: boolean;
  workspaceProjects: FullProject[];

  // Tasks
  deferredVisibleTasks: Task[];
  deferredTasks: Task[];
  visibleTasks?: Task[];
  handleAddTask: (
    type?: Task['type'],
    parentId?: string | number,
    projectId?: string,
    name?: string,
    initialData?: Partial<Task>,
    options?: { bypassDragGuard?: boolean }
  ) => Task | void | undefined;
  addIdea: (payload?: any) => Promise<string | null>;

  // Bundled Gantt state object (optional alternative to individual Gantt props)
  ganttState?: any;

  // Gantt interactions (resolved from ganttState / UIContext if omitted)
  dayWidth?: number;
  headerDates?: any[];
  months?: any[];
  dateToIndexMap?: Record<string, number>;
  activeDrag?: any;
  dragOffset?: number;
  handleGanttDragStart?: (e: React.MouseEvent, id: string | number, action: any) => void;
  handleLinkStart?: (e: React.MouseEvent, taskId: string | number) => void;
  gridStart?: Date;
  todayStr: string;
  hoveredTaskId?: string | number | null;
  setHoveredTaskId?: (id: string | number | null) => void;
  getDependencyPath?: (fromId: string | number, toId: string | number) => string | null;
  fullTaskMap?: Map<string, Task>;
  linkDrag?: any;
  linkTargetId?: string | number | null;
  handleGridMouseDown?: (e: any) => void;
  handleWheel?: (e: React.WheelEvent) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  sidebarListRef?: React.RefObject<any>;
  ganttListRef?: React.RefObject<any>;
  handleGanttScroll?: (props: { scrollOffset: number; scrollUpdateWasRequested: boolean }) => void;
  handleSidebarScroll?: (props: { scrollOffset: number; scrollUpdateWasRequested: boolean }) => void;
  listScrollTop?: number;
  taskIdMap?: Map<string, number>;
  containerHeight: number;
  hoveredDayIndex?: number | null;
  setHoveredDayIndex?: (idx: number | null) => void;
  scrollToTask?: (id: string | number, highlight?: boolean) => void;
  selectedTaskId?: string | number | null;
  scrollToToday?: (options?: { onlyHorizontal?: boolean; scrollVerticallyToActive?: boolean }) => void;
  scrollToInitialView?: () => void;
  onContainerRef?: (node: HTMLDivElement) => void;
  showDependencies?: boolean;
  handleTaskHover?: (task: Task | null, x: number, y: number) => void;

  // Kanban & List & Contacts interactions
  droppedZoneId: string | null;
  activeDragId: string | number | null;
  registerViewDragHandlers: (handlers: ViewDragHandlers | null) => void;
  exportKanbanColumnAsExcel?: (status: string, columnTitle: string, isGlobalOrTasks?: any, columnTasksOverride?: any) => Promise<void> | void;
  exportTaskTableAsExcel?: (tasksToExport?: Task[], title?: string) => Promise<void> | void;
  exportContactsAsExcel?: (isGlobal?: boolean) => Promise<void> | void;
  handleBrainstorm?: (topic: string) => void;
  handleReorderTask: (
    taskId: string | number,
    targetProjectId: string,
    targetBucketId: string,
    overId?: string | number,
    position?: 'before' | 'after' | 'end',
    mode?: 'status' | 'date' | 'custom',
    prevSort?: number,
    nextSort?: number
  ) => void;

  // Edit & Selection
  handleEditTaskMemo: (task: Task) => void;
  handleEditTaskGlobalMemo: (projectId: string, task: Task) => void;

  // Calendar specific
  otherWorkspaceProjects: FullProject[];
  calendarShowOtherWorkspace: boolean;
  setCalendarShowOtherWorkspace: (show: boolean) => void;
  activeCalendarTab: 'calendar' | 'milestones';
  setActiveCalendarTab: (tab: 'calendar' | 'milestones') => void;
  calendarShowAllTasks: boolean;

  // Metrics specific
  selectedMetricsProjectId: string;
  activeMetricsTab: MetricsTabId;
  setActiveMetricsTab: (tab: MetricsTabId) => void;
  handleDownloadRoadmap?: () => Promise<void> | void;
  handleFilterTasksFromMetrics: (priority: any, projectId?: any) => void;
  hiddenProjectIds: string[];
  setHiddenProjectIds: (ids: string[]) => void;

  // Pool specific
  activePoolTab: PoolTabId;
  setActivePoolTab: (tab: PoolTabId) => void;

  // Analysis specific
  dropdownProjects: FullProject[];
  activeAnalysisTab: AnalysisTabId;
  setActiveAnalysisTab: (tab: AnalysisTabId) => void;
  selectedAnalysisProjectId: string;
  setSelectedAnalysisProjectId: (id: string) => void;

  // Communication specific
  communicationTab?: CommunicationTabId;
  setCommunicationTab?: (tab: CommunicationTabId) => void;
  globalAllAssignees: string[];

  // AI Assistant specific
  aiAssistantTab: AIAssistantTabId;
  setAiAssistantTab: (tab: AIAssistantTabId) => void;
  selectedInboxItemId?: string | null;
  setSelectedInboxItemId?: (id: string | null) => void;
  syncInboxForce?: () => Promise<void>;
  handleAddToHistory: (projectId: string, entry: { title: string; content: string; type: string }) => void;
  handleAddAITasks?: (
    projectId: string,
    tasks: any[],
    config: any,
    description: string,
    answers: string,
    metadata?: any,
    extractedDocumentText?: string,
    inboxItem?: any,
    meetingLog?: any
  ) => void;

  // Timeline specific
  timelineRef: React.RefObject<{ scrollToToday: () => void } | null>;
  sortTasksByDate?: () => void;

  // Concept & Report specific
  conceptBuilderSection?: string | null;
  setConceptBuilderSection?: (section: string | null) => void;
  handleGenerateConcept?: (tasks: Task[]) => Promise<void> | void;
  isGeneratingConcept?: boolean;
  handleGenerateReport?: () => void;
  isGeneratingReport?: boolean;
  isReportEditing?: boolean;
  setIsReportEditing?: (isEditing: boolean) => void;
  editedReport?: string;
  setEditedReport?: (report: string) => void;

  // Global Kanban specific
  activeProjectTab?: 'all' | 'active' | 'archived';
  filterAssignee?: string;
  filterStatus?: string;
  filterType?: string;
  filterPriority?: string;

  // Mindmap specific
  upgradeIdeaToTask?: (taskId: string | number) => void;
  batchUpdateTaskPositions?: (updates: Record<string | number, { x: number; y: number }>) => void;

  // Sample Data
  deleteSampleData?: () => void;

  // Current user info for permissions
  currentUserId?: string;
  userEmail?: string;
}
